import React, {useMemo} from 'react'
import dayjs from 'dayjs'
import {Box} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {
    useGetInvoicesDashboardQuery,
    useGetMeterReadingDashboardQuery,
    useGetMeterReadingTrendQuery,
} from 'store/Store'
import KpiCard from 'components/Page/Dashboard/KpiCard'
import {
    formatMoney,
    formatNumber,
    getUnit,
    percentChange,
} from 'components/Page/Dashboard/dashboardFormat'

const gridSx = {
    display: 'grid',
    gap: {xs: 1.5, sm: 3},
    gridTemplateColumns: {xs: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)'},
}

const sum = (points, field) => points.reduce((total, point) => total + point[field], 0)

const KpiRow = ({category}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const navigate = useNavigate()
    const {data: today, isLoading: todayLoading} = useGetMeterReadingDashboardQuery({category})
    const {data: day, isLoading: dayLoading} = useGetMeterReadingTrendQuery({
        range: 'day',
        category,
    })
    const {data: week} = useGetMeterReadingTrendQuery({range: 'week', category})
    const {data: invoices, isLoading: invoicesLoading} = useGetInvoicesDashboardQuery()

    const {usageDelta, costDelta, ratePoints} = useMemo(() => {
        const dayPoints = day?.points || []
        // Compare with yesterday up to the same hour, otherwise "today so far" always looks lower.
        const sameTime = dayPoints.slice(0, dayjs().hour() + 1)
        return {
            usageDelta: percentChange(sum(dayPoints, 'usage'), sum(sameTime, 'previousUsage')),
            costDelta: percentChange(sum(dayPoints, 'cost'), sum(sameTime, 'previousCost')),
            ratePoints: (week?.points || []).map(({total, failed}) =>
                total ? ((total - failed) / total) * 100 : null
            ),
        }
    }, [day, week])

    const summary = today?.summary
    const previous = today?.previous
    const rateDelta =
        previous?.total > 0 && summary ? summary.successRate - previous.successRate : undefined
    const vsYesterday = t('dashboard.kpi.vsYesterdaySameTime')
    const trendLoading = todayLoading || dayLoading

    return (
        <Box sx={gridSx}>
            <KpiCard
                label={t('dashboard.kpi.usage')}
                value={formatNumber(summary?.usage, 1)}
                unit={getUnit(category)}
                delta={usageDelta}
                deltaLabel={vsYesterday}
                spark={(week?.points || []).map(({usage}) => usage)}
                sparkColor={palette.primary.main}
                loading={trendLoading}
            />
            <KpiCard
                label={t('dashboard.kpi.cost')}
                value={formatMoney(summary?.cost)}
                delta={costDelta}
                deltaLabel={vsYesterday}
                spark={(week?.points || []).map(({cost}) => cost)}
                sparkColor={palette.secondary.main}
                loading={trendLoading}
            />
            <KpiCard
                label={t('dashboard.kpi.success')}
                value={`${summary?.successRate ?? 0}%`}
                delta={rateDelta}
                deltaSuffix={` ${t('dashboard.kpi.pts')}`}
                deltaLabel={t('dashboard.kpi.vsYesterday')}
                goodWhen="up"
                helper={
                    summary?.total
                        ? t('dashboard.kpi.readingsCount', {count: summary.total})
                        : t('dashboard.kpi.noReadings')
                }
                spark={ratePoints}
                sparkColor={palette.success.main}
                sparkFilled={false}
                sparkDomain={[0, 100]}
                loading={todayLoading}
            />
            <KpiCard
                label={t('dashboard.kpi.unpaid')}
                value={formatMoney(invoices?.unpaid?.amount)}
                helper={t('dashboard.kpi.invoicesCount', {count: invoices?.unpaid?.count ?? 0})}
                action={
                    invoices?.unpaid?.count
                        ? {
                              label: t('dashboard.kpi.viewInvoices'),
                              onClick: () => navigate('/invoices'),
                          }
                        : undefined
                }
                loading={invoicesLoading}
            />
        </Box>
    )
}

export default KpiRow
